import { useLanguage } from '../useTextLocalizer';
import { useEffect, useState } from 'react';
import { addOnGlobalSettingsServiceUpdate, GlobalSettingsKeys } from '../../components/Settings';
import type { GlobalSettingsDispatch } from '../../components/Settings';
import { normalizeLocaleString } from '../localeUtils';
import { useOverrideFormatLocale } from './useOverrideFormatLocale';

/**
 * A way to access the format locale from Global Settings when there is no GlobalSettingsDispatchContext
 * available. The preferred method is to use the regular hooks when the context is available.
 * @param normalize Normalize the locale to use conventions for BCP-47 langauge tags.
 */
export const useFormatLocale = (normalize = true) => {
  const appLocale = useLanguage();
  const overrideLocale = useOverrideFormatLocale();

  // Alternative way to access global settings if there is not a context;
  // This approach is useful when the global settings context is eventually established in the app
  // but just not when access to it is needed.
  const [formatLocale, setFormatLocale] = useState<string | undefined>(appLocale);
  useEffect(() => {
    let unmounted = false;
    let settings: GlobalSettingsDispatch;
    const onChange = (newValue: string | undefined) => {
      if (unmounted) return;
      setFormatLocale(newValue);
    };
    const removeGlobalSettingsServiceUpdate = addOnGlobalSettingsServiceUpdate((s) => {
      if (unmounted) return;
      settings = s;
      const l = settings.get(GlobalSettingsKeys.FORMAT_LOCALE_KEY);
      // This handler runs while GlobalSettings is rendering. If we update state now we get an error
      // about updating state in another component. Use a timeout to separate the state updates.
      setTimeout(() => setFormatLocale(l), 0);
      settings.registerChangeListener(GlobalSettingsKeys.FORMAT_LOCALE_KEY, onChange);
    });
    return () => {
      unmounted = true;
      removeGlobalSettingsServiceUpdate();
      settings?.deRegisterChangeListener(GlobalSettingsKeys.FORMAT_LOCALE_KEY, onChange);
    };
  }, []);

  const locale = overrideLocale ?? formatLocale ?? appLocale;
  return normalize ? normalizeLocaleString(locale) : locale;
};
